import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import * as Sentry from '@sentry/browser'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { kUserFilesApiBasePath } from '@/constants/constants-api-paths'
import { formatUnknownError, getResponseDebugContext, getTopLevelKeys, readResponseJson } from '@/util/api/response-body'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'
import { UserFileRead, UserFileReadSchema } from '@/uppy-file-upload/schemas'
import type { FileRenamePrecondition } from '@/user_files/schemas/preconditions'
import { createUserFilesApiError, formatUserFilesErrorDetail, isExpectedUserFilesApiError } from '@/user_files/utils/api-error'

export type RenameFileRequest = {
  filename: string
  precondition?: FileRenamePrecondition
}

/**
 * Rename a file.
 *
 * @param fileId - The file ID (UUID)
 * @param request - The rename request containing the new filename
 * @returns Updated UserFileRead
 * @throws Error when user is not authenticated
 * @throws ApiError when the rename request fails
 */
export async function renameFile(fileId: string, request: RenameFileRequest): Promise<UserFileRead> {
  const sentryTransactionId = nanoid()

  try {
    const auth = getAuth()
    const token = await auth.currentUser?.getIdToken()

    if (!token) {
      throw new Error('User not authenticated')
    }

    const url = `${kPaxtonAppApiRESTBaseUrl()}${kUserFilesApiBasePath}/${encodeURIComponent(fileId)}`

    const response = await fetch(url, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        'X-Transaction-Id': sentryTransactionId,
      },
      body: JSON.stringify(request),
    })

    const { data, parseError } = await readResponseJson(response)
    if (!response.ok) {
      const errorData = parseError ? {} : data
      const error = createUserFilesApiError({ status: response.status, data: errorData, fallbackMessage: `Failed to rename file: ${response.status}` })
      const detail = formatUserFilesErrorDetail(errorData)

      if (!isExpectedUserFilesApiError(error)) {
        Sentry.withScope((scope) => {
          scope.setTags({ transaction_id: sentryTransactionId })
          Sentry.captureException(new Error(`Fetch error - status: ${response.status}, detail: ${detail}`), {
            extra: {
              ...getResponseDebugContext(response, url),
              fileId,
              responseData: errorData,
              parseError: parseError ? formatUnknownError(parseError) : null,
            },
          })
        })
      }

      throw error
    }

    if (parseError) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error('Rename file response JSON parse failed'), {
          extra: {
            ...getResponseDebugContext(response, url),
            fileId,
            requestBody: request,
            parseError: formatUnknownError(parseError),
          },
        })
      })

      throw new ApiResponsePayloadError(response.status, 'Invalid rename file response JSON')
    }

    const parsed = UserFileReadSchema.safeParse(data)
    if (!parsed.success) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error('Rename file response schema validation failed'), {
          extra: {
            ...getResponseDebugContext(response, url),
            fileId,
            requestBody: request,
            responseKeys: getTopLevelKeys(data),
            issues: parsed.error.issues,
          },
        })
      })

      throw new ApiResponsePayloadError(response.status, 'Invalid rename file response shape', parsed.error.issues)
    }

    return parsed.data
  } catch (error) {
    if (!(error instanceof ApiError)) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(error, {
          extra: { fileId, requestBody: request },
        })
      })
    }
    throw error
  }
}
